import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { customerService } from '../../../services/api';
import { parseApiFormErrors } from '../../../services/apiErrors';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';

const FORM_FIELDS = ['name', 'description'];

const DUPLICATE_INTERNAL_NAME_PREFIX = 'A department with an equivalent internal name';

// Sent by DepartmentSerializer (backend .../core/api/serializers/department.py)
// as a 409 when the name lands on a department that was soft-deleted. Nothing
// is wrong with the payload — the user just can't see that row, so we ask
// before reusing it. Keep the string in sync with the backend constant.
const DELETED_DEPARTMENT_CONFLICT_CODE = 'deleted_department_exists';

export default function DepartmentForm() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const departmentQuery = useQuery({
    queryKey: ['department', id],
    queryFn: () => customerService.getDepartment(id).then((res) => res.data),
    enabled: isEditMode,
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [internalName, setInternalName] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [touched, setTouched] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');
  // { department, intent } while the "reactivate the deleted one?" dialog is open.
  const [restoreConflict, setRestoreConflict] = useState(null);

  useEffect(() => {
    if (isEditMode && departmentQuery.data) {
      setName(departmentQuery.data.name);
      setDescription(departmentQuery.data.description ?? '');
      setInternalName(departmentQuery.data.internal_name ?? '');
    }
  }, [isEditMode, departmentQuery.data]);

  const nameTrimmed = name.trim();
  const isValid = nameTrimmed.length > 0;

  const resetFormFields = () => {
    setName('');
    setDescription('');
    setTouched(false);
  };

  const mutation = useMutation({
    mutationFn: ({ confirmRestore }) => {
      const payload = { name: nameTrimmed, description };
      return isEditMode
        ? customerService.updateDepartment(id, payload)
        : customerService.createDepartment({ ...payload, confirm_restore: Boolean(confirmRestore) });
    },
    onSuccess: (response, { intent }) => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['department', id] });
        navigate(`/departments/${response.data.uuid}`);
        return;
      }
      if (intent === 'another') {
        resetFormFields();
        setFlashMessage(t('departments.saveSuccessCreate'));
        return;
      }
      navigate('/departments', { state: { flashMessage: t('departments.saveSuccessCreate') } });
    },
    onError: (error, { intent }) => {
      if (error.response?.status === 409 && error.response.data?.code === DELETED_DEPARTMENT_CONFLICT_CODE) {
        setRestoreConflict({ department: error.response.data.department, intent });
        return;
      }
      const { fieldErrors: apiFieldErrors, formError: apiFormError } = parseApiFormErrors(error, FORM_FIELDS);
      const translated = { ...apiFieldErrors };
      if (translated.name?.startsWith(DUPLICATE_INTERNAL_NAME_PREFIX)) {
        translated.name = t('departments.validation.nameDuplicate');
      }
      setFieldErrors(translated);
      setFormError(apiFormError || (Object.keys(translated).length > 0 ? '' : t('departments.saveError')));
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    setFieldErrors({});
    setFormError('');
    if (!isValid) return;
    const intent = event.nativeEvent.submitter?.value || 'list';
    mutation.mutate({ intent, confirmRestore: false });
  };

  const confirmRestore = () => {
    const { intent } = restoreConflict;
    setRestoreConflict(null);
    mutation.mutate({ intent, confirmRestore: true });
  };

  const header = (
    <PageHeader
      title={isEditMode ? t('departments.editTitle') : t('departments.createTitle')}
      breadcrumbs={[
        { label: t('departments.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('departments.breadcrumb.list'), to: '/departments' },
        { label: isEditMode ? t('departments.breadcrumb.edit') : t('departments.breadcrumb.new') },
      ]}
    />
  );

  const isLoading = isEditMode && departmentQuery.isLoading;

  if (isLoading) {
    return (
      <BoundedPageLayout header={header}>
        <LoadingState message={t('departments.states.loading')} />
      </BoundedPageLayout>
    );
  }

  return (
    <BoundedPageLayout header={header}>
      <Stack component="form" onSubmit={handleSubmit} spacing={3} sx={{ width: '100%' }}>
        <Card>
          <CardHeader
            title={t('departments.sections.identification')}
            subheader={t('departments.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('departments.fields.name')}
                  helperText={
                    touched && !nameTrimmed
                      ? t('departments.validation.nameRequired')
                      : fieldErrors.name || t('departments.fields.nameHelper')
                  }
                  error={Boolean((touched && !nameTrimmed) || fieldErrors.name)}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setFieldErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  fullWidth
                />
                {isEditMode && (
                  <TextField
                    label={t('departments.fields.internalName')}
                    helperText={t('departments.fields.internalNameHelper')}
                    value={internalName}
                    slotProps={{ input: { readOnly: true } }}
                    fullWidth
                  />
                )}
              </Stack>
              <TextField
                label={t('departments.fields.description')}
                helperText={fieldErrors.description || t('departments.fields.descriptionHelper')}
                error={Boolean(fieldErrors.description)}
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setFieldErrors((prev) => ({ ...prev, description: undefined }));
                }}
                fullWidth
                multiline
                minRows={2}
              />
              {formError && <Alert severity="error">{formError}</Alert>}
            </Stack>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            <Button type="button" color="inherit" onClick={() => navigate(-1)}>
              {t('departments.actions.cancel')}
            </Button>
            {!isEditMode && (
              <Button
                type="submit"
                name="intent"
                value="another"
                variant="outlined"
                disabled={!isValid || mutation.isPending}
              >
                {t('departments.actions.saveAndAddAnother')}
              </Button>
            )}
            <Button
              type="submit"
              name="intent"
              value="list"
              variant="contained"
              disabled={!isValid || mutation.isPending}
            >
              {isEditMode ? t('departments.actions.saveEdit') : t('departments.actions.saveCreate')}
            </Button>
          </CardActions>
        </Card>
      </Stack>

      <Dialog open={Boolean(restoreConflict)} onClose={() => setRestoreConflict(null)}>
        <DialogTitle>{t('departments.restoreConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('departments.restoreConfirm.message', {
              name: restoreConflict?.department?.name ?? '',
              count: restoreConflict?.department?.collaborators_count ?? 0,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button color="inherit" onClick={() => setRestoreConflict(null)}>
            {t('departments.actions.cancel')}
          </Button>
          <Button variant="contained" onClick={confirmRestore} disabled={mutation.isPending}>
            {t('departments.restoreConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}
